import "styles/styles.scss";
import "styles/theme.less";
import "html5-device-mockups/dist/device-mockups.min.css";

import { API, Endpoints } from "utils/api";
import { Call, Content, FloatingButton, Icons, Loader, PagesModal, RouteContent, VideoCall } from "components";
import { ErrorBoundary, Footer, Header, Sidebar } from "screens";
import { Modal, notification } from "antd";
import { MuiThemeProvider, createTheme } from "@material-ui/core/styles";
import React, { Component } from "react";
import Routes, { localhostType, psychologistsSubDomains } from "./routes";
import {
	setAcceptedChat,
	setAudioRoom,
	setAudioSettings,
	setDnd,
	setFinishedChat,
	setFirebaseNotifications,
	setLoader,
	setMobile,
	setNotificationToken,
	setPendingCalls,
	setPendingChats,
	setPendingMessages,
	setRoom,
	setVideoSettings
} from "store/actions";

import { DateTime } from "luxon";
// @ts-ignore
import NotifSound from "assets/sounds/notif.mp3";
// @ts-ignore
import RingTone from "assets/sounds/ring.mp3";
import Socket from "utils/sockets";
import { UserPlaceholder } from "utils/utils";
// @ts-ignore
import Variables from "styles/variables.scss";
import Video from "twilio-video";
import { VideoRoomMonitor } from "@twilio/video-room-monitor";
import { connect } from "react-redux";
import { getFirebaseToken } from "../../firebase";
import logo from "assets/images/logo_small_team24@1x.png";
import { push } from "connected-react-router";
import strings from "utils/strings";

let theme: any;

export class App extends Component<any, any> {
	audio: any;
	audioPlaying: boolean;
	pendingCallToasts: Set<string>;
	dndExpiryTimer: any;
	dndMissedCalls: number;
	dndMissedChats: number;

	constructor(props: any) {
		super(props);

		this.state = {
			sidebarOpen: false,
			sidebarHidden: false,
			showNotificationModal: false,
			openVideoCallModal: false,
			totalParticipants: 0,
			totalAudioParticipants: 0,
			openAudioCallModal: false,

			reviewRoom: null,
			showRatingModal: false,
			starHovered: -1,
			stars: 0,
			tokenFetched: false
		};

		theme = createTheme({
			palette: {
				primary: {
					main: Variables.primaryColor
				}
			}
		});

		this.handleResize = this.handleResize.bind(this);
		window.addEventListener("resize", this.handleResize);

		this.audio = new Audio(RingTone);
		this.audio.loop = true;
		this.audioPlaying = false;
		this.pendingCallToasts = new Set();
		this.dndExpiryTimer = null;
		this.dndMissedCalls = 0;
		this.dndMissedChats = 0;
	}

	componentDidMount() {
		const { user } = this.props;

		this.handleResize();

		if (user) {
			this.getSockets();
		}

		// DND `until` is a plain timestamp, nothing fires when it passes: poll every 30 s (isDndActive also clears lazily).
		this.dndExpiryTimer = setInterval(() => this.expireDndIfNeeded(), 30000);
	}

	componentWillUnmount() {
		window.removeEventListener("resize", this.handleResize);
		clearInterval(this.dndExpiryTimer);
		this.stopRing();
	}

	UNSAFE_componentWillMount() {
		strings.setLanguage(this.props.language || "pt");
	}

	handleResize() {
		const { dispatch } = this.props;
		dispatch(setMobile(window.innerWidth <= 768));
	}

	async componentDidUpdate(prevProps: any) {
		const { dispatch, router, notificationToken, user, firebaseNotifications, room, audioRoom, language, dnd } =
			this.props;
		const { openVideoCallModal, openAudioCallModal } = this.state;

		if (router?.location?.pathname !== prevProps.router?.location?.pathname) {
			window.scroll({ top: 0 });

			if (Notification.permission === "default" && firebaseNotifications && user && !user?.isHR) {
				this.setState({ showNotificationModal: true });
			}
		}

		if (Notification.permission === "denied") {
			dispatch(setNotificationToken(""));
		} else if (Notification.permission === "granted" && !notificationToken && user && !this.state.tokenFetched) {
			this.setState({ tokenFetched: true });

			const notificationToken = await getFirebaseToken();

			if (notificationToken) {
				dispatch(setNotificationToken(notificationToken));

				await API.post({
					url: Endpoints.uriNotificationToken(),
					data: {
						notificationToken,
						device: "web",
						language
					}
				});
			} else {
				this.setState({ fetchingToken: false });
				console.log("Could not get firebase token");
			}
		}

		if (!room && openVideoCallModal) {
			this.setState({ openVideoCallModal: false });
		}

		if (!audioRoom && openAudioCallModal) {
			this.setState({ openAudioCallModal: false });
		}

		// Consultation ended (room and audioRoom back to null): if there are still new-call toasts open,
		// start the ring that was silenced while in consultation.
		const wasInConsultation = prevProps.room || prevProps.audioRoom;
		if (wasInConsultation && !room && !audioRoom && this.pendingCallToasts.size > 0 && this.shouldPlaySound()) {
			this.startRing();
		}

		// Logout: stop the ring and close any new-call toasts left open.
		if (prevProps.user && !user) {
			this.stopRing();
			this.pendingCallToasts.forEach((id) => notification.close(id));
			this.pendingCallToasts.clear();
			this.dndMissedCalls = 0;
			this.dndMissedChats = 0;
		}

		// DND turned on: silence a ring already playing. DND turned off (manually or expired): one summary of what
		// arrived meanwhile, then resume the ring if new-call toasts opened before DND are still there.
		if (!prevProps.dnd?.until && dnd?.until) {
			this.stopRing();
		} else if (prevProps.dnd?.until && !dnd?.until) {
			if (user) this.notifyMissedDuringDnd();
			this.syncRingWithPending();
		}

		if (!Socket.isConnected && user) {
			this.getSockets();
		}
	}

	UNSAFE_componentWillReceiveProps(nextProps: any) {
		let openVideoCallModal = nextProps.openVideoCallModal;
		let openAudioCallModal = nextProps.openAudioCallModal;
		let changed = false;

		if (
			nextProps.videoSettings?.activeVideo &&
			this.props.videoSettings?.token !== nextProps.videoSettings?.token
		) {
			openVideoCallModal = true;
			changed = true;
		}

		if (
			nextProps.audioSettings?.activeAudio &&
			this.props.audioSettings?.token !== nextProps.audioSettings?.token
		) {
			openAudioCallModal = true;
			changed = true;
		}

		if (changed) {
			this.setState({ openVideoCallModal, openAudioCallModal }, () => {
				if (openVideoCallModal) {
					this.videoConnect();
					document.body.classList.add("InCallMaximized");
				}

				if (openAudioCallModal) {
					this.audioConnect();
				}
			});
		}

		this.handleResize();
	}

	/**
	 * Central decision on whether notification sounds (SOS ring / new chat) should play.
	 * Silent while the psychologist is in a video consultation (room) or an SOS call (audioRoom),
	 * and while the manual "do not disturb" is active (see isDndActive).
	 */
	shouldPlaySound() {
		const { room, audioRoom } = this.props;

		if (room || audioRoom) {
			return false;
		}

		if (this.isDndActive()) {
			return false;
		}

		return true;
	}

	/**
	 * Manual "do not disturb" (psychologists portal): active while `dnd.until` is in the future.
	 * DND silences (no ring, no toast) but never drops: pending calls/chats still reach the store and the Home.
	 * A stale `until` is cleared here lazily as well as by the 30 s timer.
	 */
	isDndActive() {
		const { dnd } = this.props;

		if (!dnd?.until) return false;
		if (dnd.until > Date.now()) return true;

		this.expireDndIfNeeded();
		return false;
	}

	expireDndIfNeeded() {
		const { dnd, dispatch } = this.props;

		if (dnd?.until && dnd.until <= Date.now()) {
			dispatch(setDnd({ until: null }));
		}
	}

	/**
	 * DND just ended: one info toast with the totals that arrived silently, linking to the Home lists.
	 * The individual new-call / new-chat toasts are not reopened.
	 */
	notifyMissedDuringDnd() {
		const { dispatch } = this.props;
		const calls = this.dndMissedCalls;
		const chats = this.dndMissedChats;

		this.dndMissedCalls = 0;
		this.dndMissedChats = 0;

		if (calls === 0 && chats === 0) return;

		const key = "dnd-missed";
		notification.info({
			key,
			message: strings.dnd.title,
			description: (
				<React.Fragment>
					<p>{strings.formatString(strings.dnd.missedSummary, calls, chats)}</p>
					<div className="CallNotificationOptions">
						<span
							data-accept
							onClick={() => {
								notification.close(key);
								dispatch(push("/home"));
							}}
						>
							{strings.dnd.goToHome}
						</span>
					</div>
				</React.Fragment>
			),
			icon: <Icons.Bell />,
			placement: "topRight",
			duration: 0
		});
	}

	startRing() {
		if (this.audioPlaying) return;

		this.audioPlaying = true;

		const playPromise = this.audio.play();
		if (playPromise?.catch) {
			playPromise.catch(() => {
				this.audioPlaying = false;
			});
		}
	}

	stopRing() {
		this.audio.pause();
		this.audio.currentTime = 0;
		this.audioPlaying = false;
	}

	/**
	 * Ring only while there are new-call toasts open (and sound is allowed).
	 */
	syncRingWithPending() {
		if (this.pendingCallToasts.size === 0) {
			this.stopRing();
		} else if (this.shouldPlaySound()) {
			this.startRing();
		}
	}

	async ignoreCall(call: any) {
		this.pendingCallToasts.delete(call._id);
		this.stopRing();
		notification.close(call._id);

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriAudioCalls(`reject/${call._id}`),
				data: {}
			});

			if (!response.ok) {
				notification.error({
					message: strings.home.pendingCalls,
					description: response?.data?.message || strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.home.pendingCalls,
				description: response?.data?.message || strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	async acceptCall(call: any) {
		const { dispatch, room, audioRoom } = this.props;

		if (room || audioRoom) {
			return notification.warn({
				message: strings.home.pendingCalls,
				description: strings.home.cantAcceptCall,
				placement: "bottomRight",
				duration: 5
			});
		}

		this.pendingCallToasts.delete(call?._id);
		this.stopRing();

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriAudioCalls(`accept/${call?._id}`),
				data: {}
			});

			if (response.ok) {
				const { twilioRoom, userToken } = response.data.results;

				dispatch(setAudioRoom(twilioRoom));
				dispatch(setAudioSettings({ token: userToken, activeAudio: true, room: twilioRoom, user: call.user }));

				notification.close(call._id);
			} else {
				notification.error({
					message: strings.home.pendingCalls,
					description: response?.data?.message || strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.home.pendingCalls,
				description: response?.data?.message || (err as string) || strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}
	}

	async acceptChat(chat: any) {
		const { dispatch, user } = this.props;

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriChats(`accept/${chat._id}`),
				data: {}
			});

			if (response.ok) {
				dispatch(push("/chat", { chatId: chat.psychologist ? chat.socketId : `${chat.socketId}${user._id}` }));
			} else {
				notification.error({
					message: strings.sidebar.chat,
					description: response?.data?.message || strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.sidebar.chat,
				description: response?.data?.message || strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		notification.close(chat.socketId);
	}

	async getSockets() {
		const { dispatch, user, token } = this.props;
		const hostname = window.location.hostname?.split(".")?.[0];

		let source = "web";
		if (
			// @ts-ignore
			(hostname === "localhost" && localhostType === "psychologists") ||
			psychologistsSubDomains.includes(hostname)
		) {
			source = "psyc";
		}

		try {
			await Socket.initiateSocket({ source, user: user?._id, token });

			Socket.socket.offAny();
			Socket.socket.onAny((event: string, payload: any) => {

				if (!this.props.user) return;

				if (event === "voice-call" && source === "psyc") {
					const { type, record } = payload;

					dispatch(setPendingCalls(DateTime.utc().toMillis()));

					if (type === "new-call" && this.isDndActive()) {
						// DND: no ring and no toast (pendingCallToasts only tracks open toasts). The pending badge/list
						// still update through setPendingCalls above; counted for the end-of-DND summary.
						this.dndMissedCalls += 1;
					} else if (type === "new-call") {
						this.pendingCallToasts.add(record._id);

						if (this.shouldPlaySound()) {
							this.startRing();
						}

						notification.warn({
							key: record._id,
							message: strings.home.pendingCalls,
							description: (
								<React.Fragment>
									<p>
										{strings.home.newCall}{" "}
										<span
											onClick={() => dispatch(push(`/patients/${record?.user?._id}`))}
											className="CallNotificationUser"
										>
											{`${record?.user?.name} | ${record?.business?.name}${
												record?.user?.language?.nativeName
													? " | " + record?.user?.language?.nativeName
													: ""
											}`}
										</span>{" "}
										{strings.home.newCall2}
									</p>
									<div className="CallNotificationOptions">
										<span
											data-accept
											onClick={() => {
												this.acceptCall(record);
											}}
										>
											{strings.generic.accept}
										</span>
										<span
											data-ignore
											onClick={() => {
												this.ignoreCall(record);
											}}
										>
											{strings.generic.ignore}
										</span>
									</div>
								</React.Fragment>
							),
							icon: <Icons.VoiceCall />,
							placement: "topRight",
							duration: 0,
							// Toast closed by the user (X button): keep the pending set in sync
							onClose: () => {
								this.pendingCallToasts.delete(record._id);
								this.syncRingWithPending();
							}
						});
					} else if (type === "accepted-call") {
						this.pendingCallToasts.delete(record?._id || record);
						notification.close(record?._id || record);
						this.syncRingWithPending();
					} else if (type === "finish-call") {
						this.pendingCallToasts.delete(record?._id);
						notification.close(record?._id);
						this.syncRingWithPending();
					}
				} else if (event.startsWith("chat-")) {
					const { update, message } = payload;

					if (update) {
						dispatch(
							setPendingMessages({
								pending: true,
								id: event.replace("chat-", ""),
								message,
								data: DateTime.utc()
							})
						);
					}
				} else if (event === "chat") {
					const { type, record } = payload;

					if (source === "psyc") {
						if (type === "new-chat" && this.isDndActive()) {
							// DND: no toast and no sound; the pending indicator still updates.
							this.dndMissedChats += 1;
							dispatch(setPendingChats(true));
						} else if (type === "new-chat") {
							notification.warn({
								key: record.socketId,
								message: strings.home.pendingChats,
								description: (
									<React.Fragment>
										<p>
											{strings.home.newChat}{" "}
											<span
												onClick={() => dispatch(push(`/patients/${record?.user?._id}`))}
												className="CallNotificationUser"
											>
												{`${record?.user?.name} | ${record?.business?.name}${
													record?.user?.language?.nativeName
														? " | " + record?.user?.language?.nativeName
														: ""
												}`}
											</span>{" "}
											{strings.home.newChat2}
										</p>
										<div className="CallNotificationOptions">
											<span
												data-accept
												onClick={() => {
													this.acceptChat(record);
												}}
											>
												{strings.generic.accept}
											</span>
											<span
												data-ignore
												onClick={() => {
													notification.close(record.socketId);
												}}
											>
												{strings.generic.ignore}
											</span>
										</div>
									</React.Fragment>
								),
								icon: <Icons.ChatFilled color />,
								placement: "topRight",
								duration: 0
							});

							if (this.shouldPlaySound()) {
								const audio = new Audio(NotifSound);
								audio.play();
							}

							dispatch(setPendingChats(true));
						} else if (type === "accepted-chat") {
							const { record, current } = payload;
							dispatch(setAcceptedChat({ accepted: true, current, record }));

							const hasUnderscore = record.includes("_");
							notification.close(hasUnderscore ? `${record.split("_")?.[0]}_` : record);
						} else if (type === "finish-chat") {
							const { record } = payload;
							dispatch(setFinishedChat({ finished: true, record }));

							notification.close(record?.current || record);
						} else if (type === "update-chat") {
							const { record } = payload;
							dispatch(setFinishedChat({ finished: true, record }));
						}
					} else if (source === "web") {
						if (type === "accepted-chat") {
							const { record, current } = payload;
							dispatch(setAcceptedChat({ accepted: true, current, record }));

							const hasUnderscore = record.includes("_");
							notification.close(hasUnderscore ? `${record.split("_")?.[0]}_` : record);
						} else if (type === "finish-chat") {
							const { record } = payload;
							dispatch(setFinishedChat({ finished: true, record }));

							notification.close(record?.current || record);
						} else if (type === "update-chat") {
							const { record } = payload;
							dispatch(setFinishedChat({ finished: true, record }));
						}
					}
				}
			});
		} catch (err) {
			console.log("Socket error:", err as string);
		}
	}

	joinVideoCall = (room: any) => {
		const { dispatch } = this.props;

		dispatch(setRoom(room));
		VideoRoomMonitor.registerVideoRoom(room);

		let connectOptions: any = {};
		if (this.state.previewTracks) {
			connectOptions.tracks = this.state.previewTracks;
		}

		this.setState({ room });

		// Attach LocalParticipant's tracks to the DOM, if not already attached.
		const previewContainer = document.getElementById("localMedia") as HTMLElement;
		if (previewContainer && !previewContainer.querySelector("video")) {
			this.attachParticipantTracks(room.localParticipant, previewContainer, true);
		} else if (!previewContainer) {
			setTimeout(() => {
				const retryContainer = document.getElementById("localMedia");
				if (retryContainer) {
					this.attachParticipantTracks(room.localParticipant, retryContainer, true);
				}
			}, 200);
		}

		const disconnect = () => room.disconnect();

		this.setState({ totalParticipants: room.participants?.size || 0 });

		// Attach the Tracks of the room's participants.
		room.participants?.forEach((participant: any) => {
			const hostname = window.location.hostname?.split(".")?.[0];
			const previewContainer = document.getElementById("remote-media");
			this.attachParticipantTracks(participant, previewContainer);
			this.bindVideoParticipantEvents(participant);

			let source = "web";
			if (
				// @ts-ignore
				(hostname === "localhost" && localhostType === "psychologists") ||
				psychologistsSubDomains.includes(hostname)
			) {
				source = "psyc";
			}

			if (source === "web" && !this.state.reviewRoom) {
				this.setState({ reviewRoom: JSON.parse(JSON.stringify(this.props.videoSettings)) });
			}
		});

		// Attach the Tracks of the room's participants.
		room.localParticipant?.videoTracks?.forEach((participant: any) => {
			participant.setPriority("low");
		});

		// Participant joining room
		room.on("participantConnected", (participant: any) => {
			const hostname = window.location.hostname?.split(".")?.[0];
			const tracks = Array.from(participant?.tracks?.values() || []);
			const previewContainer = document.getElementById("remote-media");
			this.attachTracks(tracks, previewContainer, participant);
			this.bindVideoParticipantEvents(participant);

			this.setState((prevState: any) => ({ totalParticipants: prevState.totalParticipants + 1 }));

			let source = "web";
			if (
				// @ts-ignore
				(hostname === "localhost" && localhostType === "psychologists") ||
				psychologistsSubDomains.includes(hostname)
			) {
				source = "psyc";
			}

			if (source === "web") {
				this.setState({ reviewRoom: JSON.parse(JSON.stringify(this.props.videoSettings)) });
			}
		});

		// Attach participant's tracks to DOM when they add a track
		room.on("trackAdded", (track: any, participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.attachTracks([track], previewContainer, participant);
		});

		// Detach participant's track from DOM when they remove a track.
		room.on("trackRemoved", (track: any, participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.detachTracks([track], previewContainer);
		});

		// Detach all participant's track when they leave a room.
		room.on("participantDisconnected", (participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.detachParticipantTracks(participant, previewContainer);

			this.setState((prevState: any) => ({ totalParticipants: prevState.totalParticipants - 1 }));
		});

		// Once the local participant leaves the room, detach the Tracks
		// of all other participants, including that of the LocalParticipant.
		room.on("disconnected", () => {
			window.removeEventListener("beforeunload", disconnect);

			if (this.state.previewTracks) {
				this.state.previewTracks.forEach((track: any) => {
					track.stop();
				});
			}

			const previewContainer = document.getElementById("localMedia");
			const previewContainer2 = document.getElementById("remote-media");
			this.detachParticipantTracks(room.localParticipant, previewContainer);

			for (const elem of room.participants?.values()) {
				this.detachParticipantTracks(elem, previewContainer2);
			}

			dispatch(setRoom(null));
			dispatch(setVideoSettings(null));

			let showRatingModal = false;
			if (this.state.reviewRoom) {
				showRatingModal = true;
			}

			this.setState({
				openVideoCallModal: false,
				totalParticipants: 0,
				showRatingModal
			});
		});

		// Add a listener to disconnect from the room when a user closes their browser
		window.addEventListener("beforeunload", disconnect);
	};

	bindVideoParticipantEvents(participant: any) {
		if (!participant || participant.__videoHandlersBound) {
			return;
		}

		participant.__videoHandlersBound = true;

		participant.on("trackSubscribed", (track: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.attachTracks([track], previewContainer, participant);
		});

		participant.on("trackUnsubscribed", (track: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.detachTracks([track], previewContainer);
		});
	}

	attachParticipantTracks(participant: any, container: any, isMe: boolean = false) {
		const tracks = Array.from(participant?.tracks?.values() || []);
		this.attachTracks(tracks, container, participant, isMe);
	}

	attachTracks(tracks: any, container: any, participant?: any, isMe: boolean = false) {
		if (!container) {
			setTimeout(() => {
				const retryContainer = document.getElementById(isMe ? "localMedia" : "remote-media");
				this.attachTracks(tracks, retryContainer, participant, isMe);
			}, 200);
			return;
		}

		const tempTracks = tracks.valueOf();

		for (const track in tempTracks) {
			const temp = tempTracks[track];

			if (temp?.track) {
				if (temp?.track?.kind !== "data") {
					if (temp.track.kind === "video") {
						const trackElementClass = `video_track_${temp.trackSid || temp.track?.sid || track}`;

						if (isMe && temp.track.isEnabled) {
							const placeholder = document.getElementById("LocalPlaceholder");
							if (placeholder) {
								placeholder.classList.add("--disabled");
							}

							if (!container.getElementsByClassName(trackElementClass)?.[0]) {
								const elem = temp?.track?.attach();
								elem?.classList?.add("video_track");
								elem?.classList?.add(trackElementClass);
								elem.setAttribute("playsinline", "true");
								elem.autoplay = true;
								elem.muted = true;
								container.appendChild(elem);
							}
						} else if (!isMe && temp.track.isEnabled) {
							const placeholder = document.getElementById("ParticipantPlaceholder");
							if (placeholder) {
								placeholder.classList.add("--disabled");
							}

							if (!container.getElementsByClassName(trackElementClass)?.[0]) {
								const elem = temp?.track?.attach();
								elem?.classList?.add("video_track");
								elem?.classList?.add(trackElementClass);
								elem.setAttribute("playsinline", "true");
								elem.autoplay = true;
								container.appendChild(elem);
							}
						}

						temp.track.on("enabled", () => {
							if (temp.track?.kind === "video") {
								if (isMe) {
									const placeholder = document.getElementById("LocalPlaceholder");
									if (placeholder) {
										placeholder.classList.add("--disabled");
									}
								} else {
									const placeholder = document.getElementById("ParticipantPlaceholder");
									if (placeholder) {
										placeholder.classList.add("--disabled");
									}
								}

								if (!container.getElementsByClassName(trackElementClass)?.[0]) {
									const elem = temp.track.attach();
									elem?.classList?.add("video_track");
									elem?.classList?.add(trackElementClass);
									elem.setAttribute("playsinline", "true");
									elem.autoplay = true;
									elem.muted = Boolean(isMe);
									container.appendChild(elem);
								}
							}
						});

						temp.track.on("disabled", () => {
							if (temp.track?.kind === "video") {
								if (isMe) {
									const placeholder = document.getElementById("LocalPlaceholder");
									if (placeholder) {
										placeholder.classList.remove("--disabled");
									}
								} else {
									const placeholder = document.getElementById("ParticipantPlaceholder");
									if (placeholder) {
										placeholder.classList.remove("--disabled");
									}
								}

								const elem = container.getElementsByClassName(trackElementClass)?.[0];
								if (elem) {
									container.removeChild(elem);
								}
							}
						});

						delete tempTracks[track];
					} else if (temp.track.kind === "audio" && !isMe) {
						const elem = temp?.track?.attach();
						elem?.classList?.add(`track_${temp.trackSid}`);
						container.appendChild(elem);
						delete tempTracks[track];
					}
				}
			} else {
				setTimeout(() => {
					this.attachTracks(tempTracks, container, participant, isMe);
				}, 250);
				break;
			}
		}
	}

	detachParticipantTracks(participant: any, container: any) {
		const tracks = Array.from(participant?.tracks?.values() || []);
		this.detachTracks(tracks, container);
	}

	detachTracks(tracks: any, container: any) {
		const tempTracks = tracks.valueOf();

		if (this.state.calls > 2) return;

		for (const track in tempTracks) {
			const temp = tempTracks[track];
			const mediaTrackSid = temp.trackSid || temp?.sid || temp?.track?.sid;
			const audioElem = document.getElementsByClassName(`track_${mediaTrackSid}`)?.[0];
			if (audioElem) {
				const previewContainer = container;
				previewContainer?.removeChild(audioElem);
			}

			const videoElem = document.getElementsByClassName(`video_track_${mediaTrackSid}`)?.[0];
			if (videoElem) {
				const previewContainer = container;
				previewContainer?.removeChild(videoElem);
			}
		}
	}

	joinAudioCall = (room: any) => {
		const { dispatch } = this.props;

		dispatch(setAudioRoom(room));
		VideoRoomMonitor.registerVideoRoom(room);

		let connectOptions: any = {};
		if (this.state.previewTracks) {
			connectOptions.tracks = this.state.previewTracks;
		}

		this.setState({ audioRoom: room });

		const disconnect = () => room.disconnect();

		this.setState({ totalAudioParticipants: room.participants?.size || 0 });

		// Attach the Tracks of the room's participants.
		room.participants?.forEach((participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.attachParticipantAudioTracks(participant, previewContainer);
		});

		// Attach the Tracks of the room's participants.
		room.localParticipant?.videoTracks?.forEach((participant: any) => {
			participant.setPriority("low");
		});

		// Participant joining room
		room.on("participantConnected", (participant: any) => {
			const tracks = Array.from(participant?.tracks?.values() || []);
			const previewContainer = document.getElementById("remote-media");
			this.attachAudioTracks(tracks, previewContainer, participant);

			this.setState((prevState: any) => ({ totalAudioParticipants: prevState.totalAudioParticipants + 1 }));
		});

		// Attach participant's tracks to DOM when they add a track
		room.on("trackAdded", (track: any, participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.attachAudioTracks([track], previewContainer, participant);
		});

		// Detach participant's track from DOM when they remove a track.
		room.on("trackRemoved", (track: any, participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.detachAudioTracks([track], previewContainer);
		});

		// Detach all participant's track when they leave a room.
		room.on("participantDisconnected", (participant: any) => {
			const previewContainer = document.getElementById("remote-media");
			this.detachParticipantAudioTracks(participant, previewContainer);
			setTimeout(() => room.disconnect(), 1000);

			this.setState((prevState: any) => ({ totalAudioParticipants: prevState.totalAudioParticipants - 1 }));
		});

		// Once the local participant leaves the room, detach the Tracks
		// of all other participants, including that of the LocalParticipant.
		room.on("disconnected", () => {
			window.removeEventListener("beforeunload", disconnect);

			if (this.state.previewTracks) {
				this.state.previewTracks.forEach((track: any) => {
					track.stop();
				});
			}

			const previewContainer = document.getElementById("localMedia");
			const previewContainer2 = document.getElementById("remote-media");
			this.detachParticipantAudioTracks(room.localParticipant, previewContainer);

			for (const elem of room.participants?.values()) {
				this.detachParticipantAudioTracks(elem, previewContainer2);
			}

			dispatch(setAudioRoom(null));
			dispatch(setAudioSettings(null));

			this.setState({
				openAudioCallModal: false,
				totalAudioParticipants: 0
			});
		});

		// Add a listener to disconnect from the room when a user closes their browser
		window.addEventListener("beforeunload", disconnect);
	};

	attachParticipantAudioTracks(participant: any, container: any, isMe: boolean = false) {
		const tracks = Array.from(participant?.tracks?.values() || []);
		this.attachAudioTracks(tracks, container, participant, isMe);
	}

	attachAudioTracks(tracks: any, container: any, participant?: any, isMe: boolean = false) {
		const tempTracks = tracks.valueOf();

		for (const track in tempTracks) {
			const temp = tempTracks[track];

			if (temp?.track) {
				if (temp?.track?.kind === "audio") {
					const elem = temp?.track?.attach();
					elem?.classList?.add(`track_${temp.trackSid}`);
					container.appendChild(elem);

					break;
				}
			} else {
				setTimeout(() => {
					this.attachAudioTracks(tempTracks, container, participant);
				}, 250);
				break;
			}
		}
	}

	detachParticipantAudioTracks(participant: any, container: any) {
		const tracks = Array.from(participant?.tracks?.values() || []);
		this.detachAudioTracks(tracks, container);
	}

	detachAudioTracks(tracks: any, container: any) {
		const tempTracks = tracks.valueOf();

		if (this.state.calls > 2) return;

		for (const track in tempTracks) {
			const temp = tempTracks[track];
			const elem = document.getElementsByClassName(`track_${temp.trackSid}`)?.[0];
			if (elem) {
				const previewContainer = container;
				previewContainer?.removeChild(elem);
			}
		}
	}

	videoConnect = async () => {
		const { videoSettings } = this.props;
		let connectOptions = {
			audio: true
		} as any;

		await navigator.mediaDevices
			.getUserMedia({ audio: true, video: true })
			.then((stream) => {
				connectOptions.video = stream.getVideoTracks().length > 0;

				// Release the probe stream so Twilio can create and own the publishing tracks.
				stream.getTracks().forEach((track) => track.stop());
			})
			.catch(() => {
				connectOptions.video = false;
			});

		/*
		Connect to a room by providing the token and connection    options that include the room name and tracks. We also show an alert if an error occurs while connecting to the room.
		*/
		// setIsConnecting(true);
		Video.connect(videoSettings?.token, connectOptions).then(this.joinVideoCall, (error) => {
			alert("Could not connect to Twilio: " + error.message);
		});
	};

	audioConnect = async () => {
		const { audioSettings } = this.props;
		let connectOptions = {
			audio: true,
			video: false
		} as any;

		Video.connect(audioSettings?.token, connectOptions).then(this.joinAudioCall, (error) => {
			alert("Could not connect to Twilio: " + error.message);
		});
	};

	async sendRating() {
		const { stars, reviewRoom } = this.state;
		const { dispatch } = this.props;

		dispatch(setLoader(true));

		let response: any;
		try {
			response = await API.patch({
				url: Endpoints.uriAppointments(`${reviewRoom?.appointmentId}/review`),
				data: {
					rating: stars
				}
			});
			if (response.ok) {
				this.setState({ stars: 0, reviewRoom: null, showRatingModal: false, starHovered: -1 });

				notification.success({
					message: strings.sidebar.appointments,
					description: strings.chat.sentReview,
					placement: "bottomRight",
					duration: 5
				});
			} else {
				notification.error({
					message: strings.sidebar.appointments,
					description: response?.data?.message || strings.serverErrors.requestNotPossible,
					placement: "bottomRight",
					duration: 5
				});
			}
		} catch (err) {
			notification.error({
				message: strings.sidebar.appointments,
				description: response?.data?.message || strings.serverErrors.requestNotPossible,
				placement: "bottomRight",
				duration: 5
			});
		}

		dispatch(setLoader(false));
	}

	renderNotificationModal() {
		const { showNotificationModal } = this.state;
		const { dispatch, mobile } = this.props;

		return (
			<Modal
				centered={true}
				closable={false}
				visible={showNotificationModal}
				footer={null}
				className="AppNotificationModal"
				width={mobile ? "100%" : 575}
			>
				<div className="ModalNotificationContainer">
					<div className="ModalNotificationLogo">
						<img src={logo} alt="logo" />
					</div>
					<span className="ModalNotificationText">{strings.generic.receiveNotificationsDescription}</span>
					<div className="ModalNotificationButtons">
						<span
							onClick={() => {
								this.setState({ showNotificationModal: false });
								dispatch(setFirebaseNotifications(false));
							}}
						>
							{strings.generic.noThankYou}
						</span>
						<button
							onClick={() => {
								this.setState({ showNotificationModal: false });
								dispatch(setFirebaseNotifications(false));

								getFirebaseToken();
							}}
							className="ModalNotificationClose"
						>
							{strings.generic.permit}
						</button>
					</div>
				</div>
			</Modal>
		);
	}

	renderRatingModal() {
		const { showRatingModal, starHovered, stars, reviewRoom } = this.state;
		const { mobile } = this.props;
		const { room } = reviewRoom || {};
		const { user: psychologist } = room || {};

		if (!reviewRoom) return;

		return (
			<Modal
				centered={true}
				closable={true}
				onCancel={async () => {
					this.setState({ showRatingModal: false, starHovered: -1, stars: 0, reviewRoom: null });
				}}
				visible={showRatingModal}
				footer={null}
				width={mobile ? "100%" : 400}
			>
				<div className="RatingChatModal">
					<h2>{strings.chat.howDidItGo}</h2>
					<div className="RatingPsychologist">
						<img src={psychologist?.photo || UserPlaceholder} alt="psychologist avatar" />
						<h2>{psychologist?.externalName || psychologist?.name}</h2>
					</div>
					<div className="RatingStars">
						{Array(5)
							.fill("")
							.map((_: any, index: number) => {
								let className = "";
								if (index <= starHovered) {
									className = "-btn--hovered";
								} else if (index + 1 <= stars) {
									className = "-btn--selected";
								}

								return (
									<button
										key={`star_${index}`}
										className={className}
										onMouseEnter={() => this.setState({ starHovered: index })}
										onMouseLeave={() => this.setState({ starHovered: -1 })}
										onClick={() => this.setState({ stars: index + 1 })}
									>
										<Icons.Star />
									</button>
								);
							})}
					</div>
					<button
						onClick={async () => {
							if (stars) {
								this.sendRating();
								this.setState({ showRatingModal: false, stars: null, reviewRoom: null });
							} else {
								notification.warn({
									message: strings.sidebar.appointments,
									description: strings.chat.selectRating,
									placement: "bottomRight",
									duration: 5
								});
							}
						}}
						className="RatingRate"
					>
						{strings.generic.confirm}
					</button>
				</div>
			</Modal>
		);
	}

	render() {
		const { openVideoCallModal, openAudioCallModal, totalParticipants, totalAudioParticipants, userHasVideo } =
			this.state;
		const { user, token, mobile } = this.props;
		const isAuthenticated = Boolean(user) && Boolean(token);

		return (
			<MuiThemeProvider theme={theme}>
				<div className="AppWrapper">
					{isAuthenticated && <Sidebar location={window.location.pathname} />}
					<Content>
						{isAuthenticated && <Header />}
						<RouteContent>
							<ErrorBoundary>
								<Routes />
							</ErrorBoundary>
						</RouteContent>
					</Content>
				</div>
				{mobile ? null : <Footer />}
				<Loader />
				{this.renderNotificationModal()}
				<VideoCall
					openVideoCallModal={openVideoCallModal}
					totalParticipants={totalParticipants}
					participantAddedVideo={userHasVideo}
				/>
				<Call openAudioCallModal={openAudioCallModal} totalParticipants={totalAudioParticipants} />
				<FloatingButton location={window.location.pathname} />
				<PagesModal />
				{this.renderRatingModal()}
			</MuiThemeProvider>
		);
	}
}

const mapStateToProps = (state: any) => ({
	router: state.router,
	loader: state.loader,
	language: state.language,
	user: state.user,
	token: state.token,
	notificationToken: state.notificationToken,
	firebaseNotifications: state.firebaseNotifications,
	videoSettings: state.videoSettings,
	audioSettings: state.audioSettings,
	room: state.room,
	audioRoom: state.audioRoom,
	mobile: state.mobile,
	dnd: state.dnd
});

export default connect(mapStateToProps)(App);
